import {
  BoxGeometry,
  CapsuleGeometry,
  Color,
  ConeGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  SphereGeometry,
  Vector3,
} from 'three';
import { Pool } from '../util/pool.js';
import { choose, clamp, lerp, rand } from '../util/math.js';
import { resolveObstacleCollision } from '../world/obstacles.js';
import { createEnemyAnim, triggerEnemyHurt, updateEnemyAnim } from './enemy_anim.js';
import { applyEnemyModel, removeAssetModel } from './enemy_models.js';

const enemyDefs = {
  grunt: { label: 'Walker', color: 0x82f060, head: 0xb2ff92, hp: 1, speed: 1, radius: 0.74, damage: 7, score: 35, xp: 5 },
  runner: { label: 'Runner', color: 0x53ffb5, head: 0xb2ff92, hp: 1, speed: 1.28, radius: 0.68, damage: 8, score: 45, xp: 6 },
  brute: { label: 'Brute', color: 0xffd166, head: 0xffe3a6, hp: 4, speed: 0.72, radius: 0.94, damage: 12, score: 85, xp: 12, elite: true },
  spitter: { label: 'Spitter', color: 0xa178ff, head: 0xd7bdff, hp: 2, speed: 0.8, radius: 0.82, damage: 9, score: 65, xp: 9, ranged: true },
  charger: { label: 'Charger', color: 0xff4f68, head: 0xff9aaa, hp: 3, speed: 1.05, radius: 0.84, damage: 13, score: 75, xp: 10 },
  swarm: { label: 'Swarm', color: 0x9cfffa, head: 0xd8fffb, hp: 1, speed: 1.45, radius: 0.52, damage: 5, score: 24, xp: 3 },
};

const bossDefs = {
  charger: { label: 'Breaker Charger', color: 0xff4f68, head: 0xff9aaa, hp: 10, speed: 1.18, radius: 1.55, damage: 22, score: 470, xp: 70, elite: true, scale: 2.05 },
  summoner: { label: 'Signal Summoner', color: 0xa178ff, head: 0xd7bdff, hp: 12, speed: 0.7, radius: 1.72, damage: 16, score: 500, xp: 76, ranged: true, elite: true, scale: 2.18 },
  sniper: { label: 'Deadeye Sniper', color: 0x6aa9ff, head: 0xc7deff, hp: 9, speed: 0.62, radius: 1.46, damage: 28, score: 510, xp: 78, ranged: true, elite: true, scale: 1.92 },
  juggernaut: { label: 'Loop Juggernaut', color: 0xffd166, head: 0xffe3a6, hp: 16, speed: 0.55, radius: 1.95, damage: 24, score: 560, xp: 84, elite: true, scale: 2.45 },
};

const geometries = {
  body: new CapsuleGeometry(0.42, 0.7, 4, 8),
  head: new SphereGeometry(0.32, 12, 10),
  arm: new BoxGeometry(0.18, 0.62, 0.18),
  leg: new BoxGeometry(0.2, 0.58, 0.22),
  claw: new ConeGeometry(0.08, 0.35, 6),
};

const toPlayer = new Vector3();
const avoid = new Vector3();
const pursuit = new Vector3();
const side = new Vector3();

let enemyState = null;

export function initEnemies(world, options = {}) {
  const capacity = options.capacity ?? 96;
  const pool = new Pool(createEnemyEntity, resetEnemyEntity, Math.min(12, capacity), capacity);
  enemyState = {
    world,
    pool,
    enemies: [],
  };
  return enemyState;
}

export function getEnemies() {
  if (!enemyState) {
    throw new Error('[enemies] initEnemies() must run before getEnemies().');
  }
  return enemyState.enemies;
}

export function chooseEnemyType(wave = 1, index = 0) {
  const pool = ['grunt', 'grunt', 'runner', 'swarm'];   // runners/swarm from wave 1
  if (wave >= 2) pool.push('brute', 'runner');          // brute from wave 2
  if (wave >= 3) pool.push('spitter', 'charger');
  if (wave >= 4) pool.push('charger', 'swarm', 'swarm');
  if (index % 8 === 0 && wave > 1) return choose(['brute', 'charger', 'runner']);
  if (index % 7 === 3 && wave > 2) return choose(['spitter', 'swarm', 'swarm']);
  return choose(pool);
}

export function spawnEnemy(spec = {}, options = {}) {
  const state = enemyState;
  if (!state) throw new Error('[enemies] initEnemies() must run before spawnEnemy().');

  const game = options.game || {};
  const player = options.player || { pos: new Vector3() };
  const boss = !!spec.boss;
  const type = spec.type || 'grunt';
  const def = boss
    ? bossDefs[spec.variant] || { label: 'Warden', color: 0xff3156, head: 0xff8095, hp: 12, speed: 0.72, radius: 1.75, damage: 18, score: 440, xp: 65, ranged: true, elite: true, scale: 2.28 }
    : enemyDefs[type] || enemyDefs.grunt;
  const entity = state.pool.acquire();
  if (!entity) return null;

  configureEnemyModel(entity, def, boss, boss ? 'boss' : type);

  const edge = Math.max(12, state.world.half - 4);
  const pos = spec.pos ? spec.pos.clone() : randomEdgePosition(edge);
  entity.group.position.copy(pos);
  entity.group.rotation.y = Math.atan2(player.pos.x - pos.x, player.pos.z - pos.z);
  entity.group.visible = true;
  if (!entity.group.parent) state.world.sceneRoot.add(entity.group);

  const wave = game.wave || 1;
  const hp = boss
    ? 120 + wave * 36 + (game.mapTier || 0) * 30
    : Math.max(1, Math.ceil((def.hp + Math.floor(wave / 2) * 0.85) * (1 + wave * 0.18) * (game.sectorThreat || 1)));

  Object.assign(entity, {
    pos,
    radius: def.radius,
    hp,
    maxHp: hp,
    speed: boss ? 4.0 + wave * 0.08 : (5.0 + wave * 0.24) * def.speed * Math.min(1.18, 0.92 + (game.sectorThreat || 1) * 0.08),
    boss,
    bossVariant: boss ? (spec.variant || 'warden') : null,
    elite: !!def.elite && !boss,
    ranged: !!def.ranged || boss || (wave >= 4 && (!!def.elite || type === 'charger')),
    type: boss ? (spec.variant || 'boss') : type,
    rawType: type,
    label: boss ? def.label : def.label,
    alive: true,
    contactDamage: boss ? 18 : def.damage,
    xpValue: boss ? 70 + wave * 5 : def.xp + Math.floor(wave * 0.7),
    scoreValue: boss ? 450 + wave * 40 : def.score,
    attackCooldown: def.ranged ? rand(0.55, 1.4) : rand(0.15, 0.75),
    slamCooldown: boss ? 2.1 : 0,
    specialCooldown: rand(1, 2.6),
    slowTimer: 0,
    chargeTimer: 0,
    strafeSign: 1,
    wobble: Math.random() * Math.PI * 2,
  });
  entity.anim = createEnemyAnim(entity.group, type);
  entity.vel.set(0, 0, 0);
  state.enemies.push(entity);
  if (boss) game.boss = entity;
  return entity;
}

export function updateEnemies(dt, options = {}) {
  const state = enemyState;
  if (!state) return;

  const {
    player,
    game = {},
    onContact = null,
    onRangedAttack = null,
  } = options;
  if (!player) return;

  for (const enemy of state.enemies) {
    if (!enemy.alive) continue;
    updateEnemy(enemy, dt, player, game, onContact, onRangedAttack);
  }

  for (let i = state.enemies.length - 1; i >= 0; i -= 1) {
    if (!state.enemies[i].alive) releaseEnemy(state.enemies[i], i);
  }
}

export function damageEnemy(enemy, amount, options = {}) {
  if (!enemy?.alive) return false;

  enemy.hp -= Math.max(0.1, amount);
  enemy.flash = 0.12;
  enemy.hitSquash = Math.min(0.18, (enemy.hitSquash || 0) + (enemy.boss ? 0.035 : 0.07));
  triggerEnemyHurt(enemy.anim);
  options.onHit?.(enemy);
  if (enemy.hp <= 0) {
    killEnemy(enemy, options);
    return true;
  }
  return false;
}

export function killEnemy(enemy, options = {}) {
  if (!enemy?.alive) return;
  enemy.alive = false;
  options.onKill?.(enemy);
}

export function enemyAccent(enemy) {
  if (enemy?.boss) return 0xff3156;
  const def = enemyDefs[enemy?.rawType] || enemyDefs[enemy?.type] || enemyDefs.grunt;
  return def.color;
}

export function clearEnemies() {
  const state = enemyState;
  if (!state) return;
  for (let i = state.enemies.length - 1; i >= 0; i -= 1) releaseEnemy(state.enemies[i], i);
}

function updateEnemy(enemy, dt, player, game, onContact, onRangedAttack) {
  enemy.wobble += dt * (enemy.boss ? 2.5 : enemy.rawType === 'runner' ? 8.2 : 5.4);
  toPlayer.copy(player.pos).sub(enemy.pos);
  toPlayer.y = 0;
  const distance = Math.max(0.001, toPlayer.length());
  const dir = toPlayer.multiplyScalar(1 / distance);

  avoid.set(0, 0, 0);
  for (const other of enemyState.enemies) {
    if (other === enemy || !other.alive) continue;
    const dx = enemy.pos.x - other.pos.x;
    const dz = enemy.pos.z - other.pos.z;
    const d2 = dx * dx + dz * dz;
    const min = enemy.radius + other.radius + 0.25;
    if (d2 > 0.0001 && d2 < min * min) {
      const d = Math.sqrt(d2);
      avoid.x += (dx / d) * (min - d);
      avoid.z += (dz / d) * (min - d);
    }
  }

  dir.addScaledVector(avoid, 0.75).normalize();
  pursuit.copy(dir);
  if (enemy.ranged && distance < 9.5) pursuit.multiplyScalar(-0.55);
  updateSpecialMovement(enemy, dt, distance, dir, player);

  const contactRadius = enemy.radius + player.radius + 0.26;
  if (distance < contactRadius) {
    updateEnemyAttacks(enemy, dt, distance, player, game, onContact, onRangedAttack);
    repelEnemyFromPlayer(enemy, player, contactRadius + 0.45);
    updateEnemyScale(enemy, dt);
    enemy.group.position.copy(enemy.pos);
    enemy.group.position.y = Math.sin(enemy.wobble) * (enemy.boss ? 0.04 : 0.06);
    enemy.group.rotation.y = Math.atan2(dir.x, dir.z);
    updateEnemyAnim(enemy.anim, enemy.vel, true, dt);
    updateEnemyMaterials(enemy, dt, game);
    return;
  }

  const speed = enemy.speed
    * (enemy.hp < enemy.maxHp * 0.35 ? 1.14 : 1)
    * (enemy.slowTimer > 0 ? 0.42 : 1)
    * (game.overdriveTime > 0 ? 0.54 : 1)
    * (enemy.chargeTimer > 0 ? 3.2 : 1);
  enemy.slowTimer = Math.max(0, (enemy.slowTimer || 0) - dt);
  updateEnemyScale(enemy, dt);
  enemy.vel.x = lerp(enemy.vel.x, pursuit.x * speed, 1 - Math.exp(-dt * 8));
  enemy.vel.z = lerp(enemy.vel.z, pursuit.z * speed, 1 - Math.exp(-dt * 8));
  enemy.pos.addScaledVector(enemy.vel, dt);
  resolveObstacleCollision(enemy.pos, enemy.radius, enemyState.world);
  enemy.pos.x = clamp(enemy.pos.x, -enemyState.world.half + enemy.radius, enemyState.world.half - enemy.radius);
  enemy.pos.z = clamp(enemy.pos.z, -enemyState.world.half + enemy.radius, enemyState.world.half - enemy.radius);
  const contactDistance = Math.hypot(player.pos.x - enemy.pos.x, player.pos.z - enemy.pos.z);
  enemy.group.position.copy(enemy.pos);
  enemy.group.position.y = Math.sin(enemy.wobble) * (enemy.boss ? 0.04 : 0.06);
  enemy.group.rotation.y = Math.atan2(dir.x, dir.z);
  updateEnemyAnim(enemy.anim, enemy.vel, contactDistance < enemy.radius + player.radius + 0.8, dt);
  updateEnemyMaterials(enemy, dt, game);
  updateEnemyAttacks(enemy, dt, contactDistance, player, game, onContact, onRangedAttack);
}

function repelEnemyFromPlayer(enemy, player, minDistance) {
  const dx = enemy.pos.x - player.pos.x;
  const dz = enemy.pos.z - player.pos.z;
  const len = Math.hypot(dx, dz) || 1;
  enemy.pos.x = player.pos.x + (dx / len) * minDistance;
  enemy.pos.z = player.pos.z + (dz / len) * minDistance;
  enemy.vel.x = (dx / len) * Math.max(4, enemy.speed * 0.5);
  enemy.vel.z = (dz / len) * Math.max(4, enemy.speed * 0.5);
}

function updateSpecialMovement(enemy, dt, distance, dir, player) {
  // Charger: burst sprint toward player
  if (enemy.rawType === 'charger') {
    enemy.specialCooldown -= dt;
    if (enemy.chargeTimer > 0) {
      enemy.chargeTimer -= dt;
      pursuit.copy(enemy.chargeDir);
    } else if (enemy.specialCooldown <= 0 && distance > 5 && distance < 22) {
      enemy.chargeDir.copy(dir);
      enemy.chargeTimer = 0.62;
      enemy.specialCooldown = rand(2.2, 3.8);
    }
    return;
  }

  // Runner: zigzag strafing — switch flank every 0.6–1.2 s
  if (enemy.rawType === 'runner') {
    enemy.specialCooldown -= dt;
    if (enemy.specialCooldown <= 0) {
      enemy.strafeSign = -(enemy.strafeSign || 1);
      enemy.specialCooldown = rand(0.55, 1.2);
    }
    const s = enemy.strafeSign || 1;
    const bx = dir.x * 0.62 + (-dir.z) * s * 0.78;
    const bz = dir.z * 0.62 + dir.x * s * 0.78;
    const len = Math.hypot(bx, bz) || 1;
    pursuit.set(bx / len, 0, bz / len);
    return;
  }

  // Swarm: spiral/flank approach — offset perpendicular based on wobble
  if (enemy.rawType === 'swarm' && distance < 18) {
    const offset = Math.sin(enemy.wobble * 2 + enemy.attackCooldown * 4) * 0.65;
    const fx = dir.x + (-dir.z) * offset;
    const fz = dir.z + dir.x * offset;
    const len = Math.hypot(fx, fz) || 1;
    pursuit.set(fx / len, 0, fz / len);
    return;
  }

  // Wraith teleport (legacy)
  if (enemy.rawType === 'wraith' && enemy.specialCooldown <= 0 && distance > 8) {
    side.set(-dir.z, 0, dir.x).multiplyScalar(rand(-5, 5));
    enemy.pos.copy(player.pos).addScaledVector(dir, -rand(6, 10)).add(side);
  }
}

function updateEnemyAttacks(enemy, dt, distance, player, game, onContact, onRangedAttack) {
  enemy.attackCooldown -= dt;
  if (enemy.ranged && distance < 28 && distance > 5.5 && enemy.attackCooldown <= 0) {
    onRangedAttack?.(enemy);
    enemy.attackCooldown = clamp(1.45 - (game.wave || 1) * 0.035, 0.62, 1.7);
  }
  if (distance < enemy.radius + player.radius + 0.26 && enemy.attackCooldown <= 0) {
    onContact?.(enemy, enemy.contactDamage);
    enemy.attackCooldown = enemy.boss ? 0.82 : enemy.ranged ? 0.8 : 0.55;
  }
}

function updateEnemyMaterials(enemy, dt, game) {
  if (enemy.flash > 0) {
    enemy.flash -= dt;
    for (const mat of enemy.mats) {
      mat.emissive.setHex(0xffffff);
      mat.emissiveIntensity = enemy.flash * (enemy.boss ? 5 : 8);
    }
    return;
  }
  for (const mat of enemy.mats) {
    mat.emissiveIntensity = enemy.boss ? 0.24 : enemy.ranged ? 0.18 + Math.sin((game.elapsed || 0) * 6 + enemy.wobble) * 0.04 : enemy.elite ? 0.14 : 0;
  }
}

function updateEnemyScale(enemy, dt) {
  const base = enemy.baseScale || 1;
  enemy.hitSquash = Math.max(0, (enemy.hitSquash || 0) - dt * 0.62);
  const squash = enemy.hitSquash;
  enemy.group.scale.set(base * (1 + squash), base * (1 - squash * 0.18), base * (1 + squash));
}

function createEnemyEntity() {
  const group = makeZombieModel();
  return {
    group,
    mats: group.userData.mats,
    pos: new Vector3(),
    vel: new Vector3(),
    chargeDir: new Vector3(),
    alive: false,
  };
}

function resetEnemyEntity(enemy) {
  enemy.group.visible = false;
  enemy.group.scale.setScalar(1);
  removeAssetModel(enemy);
  enemy.vel.set(0, 0, 0);
  enemy.alive = false;
  enemy.flash = 0;
  enemy.baseScale = 1;
  enemy.hitSquash = 0;
  enemy.slowTimer = 0;
  enemy.chargeTimer = 0;
  enemy.strafeSign = 1;
}

function releaseEnemy(enemy, index) {
  enemyState.world.sceneRoot.remove(enemy.group);
  enemyState.enemies.splice(index, 1);
  enemyState.pool.release(enemy);
}

function makeZombieModel() {
  const group = new Group();
  const bodyMat = new MeshStandardMaterial({ color: 0x82f060, roughness: 0.9, metalness: 0 });
  const headMat = new MeshStandardMaterial({ color: 0xb2ff92, roughness: 0.9, metalness: 0 });
  bodyMat.emissive = new Color(0x000000);
  headMat.emissive = new Color(0x000000);
  const body = addPart(group, geometries.body, bodyMat, [0, 1.06, 0]);
  addPart(group, geometries.head, headMat, [0, 1.78, 0]);
  const leftArm = addPart(group, geometries.arm, bodyMat, [-0.48, 1.1, -0.12], [0.25, 0, 0.32]);
  const rightArm = addPart(group, geometries.arm, bodyMat, [0.48, 1.1, -0.12], [0.25, 0, -0.32]);
  const leftLeg = addPart(group, geometries.leg, bodyMat, [-0.18, 0.38, 0.05]);
  const rightLeg = addPart(group, geometries.leg, bodyMat, [0.18, 0.38, 0.05]);
  group.userData.mats = [bodyMat, headMat];
  group.userData.parts = { body, leftArm, rightArm, leftLeg, rightLeg };
  return group;
}

function configureEnemyModel(enemy, def, boss, assetType) {
  const [bodyMat, headMat] = enemy.mats;
  bodyMat.color.setHex(def.color);
  headMat.color.setHex(def.head);
  bodyMat.emissive.setHex(boss ? 0x280006 : def.ranged ? 0x1e0a34 : def.elite ? 0x2a1a00 : 0x000000);
  headMat.emissive.setHex(bodyMat.emissive.getHex());
  enemy.baseScale = def.scale || 1;
  enemy.hitSquash = 0;
  enemy.group.scale.setScalar(enemy.baseScale);
  applyEnemyModel(enemy, assetType, boss ? 3 : Math.max(1.2, def.radius * 2.2));
}

function randomEdgePosition(edge) {
  const pos = new Vector3();
  if (Math.random() > 0.5) {
    pos.x = choose([-edge, edge]);
    pos.z = rand(-edge, edge);
  } else {
    pos.z = choose([-edge, edge]);
    pos.x = rand(-edge, edge);
  }
  return pos;
}

function addPart(group, geometry, material, position, rotation = [0, 0, 0]) {
  const mesh = new Mesh(geometry, material);
  mesh.userData.proceduralEnemy = true;
  mesh.position.set(position[0], position[1], position[2]);
  mesh.rotation.set(rotation[0], rotation[1], rotation[2]);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  return mesh;
}
